/**
 * New on Hike Cinema — turns one admin action into the PATCH /admin/v1/curation
 * body the contract expects, and mirrors its 400/409s so they are caught before
 * the round trip. Pure functions (no React, no store) so the rules are testable.
 *
 * The rail's three admin states:
 *   pins         → permanent, each at the exact position the admin chose (gaps
 *                  allowed — auto-fill flows around them). Full replace.
 *   excluded_ids → kept out of the rail and out of auto-fill. Full replace.
 *   reorder      → temporary 15-day holds at any position a pin does not hold.
 *                  A DELTA: only entries touched in this call are sent.
 *
 * A pin is always planned and sent at the exact position the admin picked — the
 * screen's rank and the payload's `position` are the same number, never a
 * rewritten one. Every pin write sends `pins` (series_id + position) AND the
 * same pins as `series_ids` in position order, for a server that predates
 * `pins`. Such a server stores pins from #1 down and will land a pin at a rank
 * other than the one sent; that is a backend gap to close (`pins` support), not
 * something this layer papers over by sending a different position.
 *
 * Sending the pins AND excluded_ids both empty in one call is the contract's
 * Reset (it also wipes every hold), so every other change sends only the arrays
 * it actually changed, and `planChange` refuses to produce that body otherwise.
 */

export const CURATION_TYPE = 'new_release';
export const RAIL_SIZE = 10;
export const NAME_MAX_LENGTH = 100;
export const HOLD_DAYS = 15;

const DAY_MS = 24 * 60 * 60 * 1000;
const POSITIONS = Array.from({ length: RAIL_SIZE }, (_, i) => i + 1);

const toIds = (list) => (Array.isArray(list) ? list : [])
    .map(Number)
    .filter(Number.isFinite);

const byPosition = (a, b) => a.position - b.position;

const toPins = (data) => {
    if (Array.isArray(data?.pins)) {
        return data.pins
            .map((p) => ({ seriesId: Number(p?.series_id), position: Number(p?.position) }))
            .filter((p) => Number.isFinite(p.seriesId) && Number.isFinite(p.position))
            .sort(byPosition);
    }
    // Older servers only send series_ids, where index + 1 is the position.
    return toIds(data?.series_ids).map((seriesId, i) => ({ seriesId, position: i + 1 }));
};

/** GET / PATCH `data` → the state `planChange` works on. */
export const normaliseCuration = (data) => {
    const pins = toPins(data);
    return {
        // Comes back as a string ("2"); writes want a number.
        configId: data?.id != null && data.id !== "" && Number.isFinite(Number(data.id)) ? Number(data.id) : null,
        name: data?.name || '',
        isActive: !(data?.is_active === false || data?.is_active === 0),
        // False on servers that predate exact pin positions: there pins always
        // sit at #1..#N in order.
        exactPins: Array.isArray(data?.pins),
        pins,
        seriesIds: pins.map((p) => p.seriesId),
        excludedIds: toIds(data?.excluded_ids),
        holds: (Array.isArray(data?.reorder) ? data.reorder : [])
            .map((r) => ({
                seriesId: Number(r?.series_id),
                position: Number(r?.position),
                expiresAt: r?.expires_at || null,
            }))
            .filter((h) => Number.isFinite(h.seriesId) && Number.isFinite(h.position))
            .sort(byPosition),
    };
};

/** @returns {string|null} the problem with the name, or null when it is valid. */
export const validateRailName = (name = '') => {
    const trimmed = String(name).trim();
    if (!trimmed) return 'Name is missing';
    if (trimmed.length > NAME_MAX_LENGTH) return `The name must be ${NAME_MAX_LENGTH} characters or fewer.`;
    return null;
};

/**
 * A hold runs for exactly HOLD_DAYS from the save, so the countdown can never
 * legitimately read more than that. What's left is `expires_at` (stamped by the
 * server) minus the browser's own clock, and the two are never in step: a clock
 * a few minutes behind turns 15 days into "16 days left" once rounded up, and a
 * badly-set system date reads months. So the span is clamped to the hold's own
 * length — the expiry itself is enforced server-side, not here.
 * @returns {{ expired: boolean, daysLeft: number } | null} null when there is no usable date.
 */
export const holdTimeLeft = (expiresAt, now = Date.now()) => {
    const at = Date.parse(expiresAt);
    if (!Number.isFinite(at)) return null;
    const ms = at - now;
    return { expired: ms <= 0, daysLeft: Math.min(HOLD_DAYS, Math.max(0, Math.ceil(ms / DAY_MS))) };
};

/** Pins left once `seriesId` is taken out of them (it may not be pinned at all). */
export const pinsWithout = (state, seriesId) => state.pins.filter((p) => p.seriesId !== seriesId);

/** The pin at `position`, or undefined. */
export const pinAt = (state, position) => state.pins.find((p) => p.position === position);

/** The position `seriesId` is pinned at, or null. */
export const pinPositionOf = (state, seriesId) => state.pins.find((p) => p.seriesId === seriesId)?.position ?? null;

/** The pins once `seriesId` is taken out; the rest keep their own positions. */
const pinsLeft = (state, seriesId) => pinsWithout(state, seriesId);

/**
 * Positions a pin of `seriesId` may take: any slot no other pin holds, and an
 * existing pin may take another pin's slot (the two swap).
 */
export const pinPositions = (state, seriesId) => {
    if (pinPositionOf(state, seriesId) !== null) return POSITIONS;
    return POSITIONS.filter((p) => !pinAt(state, p));
};

/** Positions a hold of `seriesId` may take: any slot the pins left behind don't hold. */
export const holdPositions = (state, seriesId) => {
    const left = pinsLeft(state, seriesId);
    return POSITIONS.filter((p) => !left.some((pin) => pin.position === p));
};

const pinsBody = (pins) => {
    const sorted = [...pins].sort(byPosition);
    return {
        pins: sorted.map((p) => ({ series_id: p.seriesId, position: p.position })),
        series_ids: sorted.map((p) => p.seriesId),
    };
};

const samePins = (a, b) => a.pins.length === b.pins.length
    && b.pins.every((p) => pinPositionOf(a, p.seriesId) === p.position);

const withPins = (state, pins) => {
    const sorted = [...pins].sort(byPosition);
    return { ...state, pins: sorted, seriesIds: sorted.map((p) => p.seriesId) };
};

/**
 * Applies one drag to a local draft of the rail — nothing is sent. A pin moves
 * and stays a pin (onto another pin = swap, onto a hold = ends that hold);
 * anything else becomes a 15-day hold at `position`, ending any other hold there.
 * @returns {{ state: object, error: string|null }} `state` is unchanged on error.
 */
export const applyMove = (state, seriesId, position) => {
    if (!validPosition(position)) return { state, error: `Position must be between 1 and ${RAIL_SIZE}.` };
    const id = Number(seriesId);
    const current = pinPositionOf(state, id);
    const occupant = pinAt(state, position);

    if (current !== null) {
        if (current === position) return { state, error: null };
        const pins = state.pins.map((p) => {
            if (p.seriesId === id) return { ...p, position };
            if (occupant && p.seriesId === occupant.seriesId) return { ...p, position: current };
            return p;
        });
        return { state: { ...withPins(state, pins), holds: state.holds.filter((h) => h.position !== position) }, error: null };
    }

    if (occupant) return { state, error: `#${position} is pinned. Unpin it first, or drop the series somewhere else.` };
    const own = state.holds.find((h) => h.seriesId === id);
    if (own?.position === position) return { state, error: null };
    const holds = [
        ...state.holds.filter((h) => h.seriesId !== id && h.position !== position),
        // No expiry yet: the 15 days start when the move is saved.
        { seriesId: id, position, expiresAt: null },
    ].sort(byPosition);
    return { state: { ...state, holds }, error: null };
};

/**
 * The moves a draft makes against the saved rail, for the save summary.
 * @returns {{ seriesId: number, position: number, kind: 'pin'|'hold' }[]}
 */
export const describeMoves = (saved, draft) => [
    ...draft.pins
        .filter((p) => pinPositionOf(saved, p.seriesId) !== p.position)
        .map((p) => ({ seriesId: p.seriesId, position: p.position, kind: 'pin' })),
    ...draft.holds
        .filter((h) => saved.holds.find((s) => s.seriesId === h.seriesId)?.position !== h.position)
        .map((h) => ({ seriesId: h.seriesId, position: h.position, kind: 'hold' })),
].sort(byPosition);

/**
 * One PATCH body for every drag staged in `draft` since `saved`: the full pin
 * list when any pin moved, plus a reorder entry for each new or moved hold.
 * @returns {{ body: object|null, evicted: object[], error: string|null }}
 *   `body` is null (without an error) when the draft changes nothing.
 */
export const planMoves = (saved, draft) => {
    if (saved?.configId == null) return { body: null, evicted: [], error: 'The rail has not loaded yet.' };
    const body = { curation_type: CURATION_TYPE, curation_config_id: saved.configId };
    const pinsMoved = !samePins(saved, draft);
    if (pinsMoved) Object.assign(body, pinsBody(draft.pins));
    const reorder = describeMoves(saved, draft)
        .filter((m) => m.kind === 'hold')
        .map((m) => ({ series_id: m.seriesId, position: m.position }));
    if (reorder.length) body.reorder = reorder;
    if (!pinsMoved && !reorder.length) return { body: null, evicted: [], error: null };
    return {
        body,
        evicted: saved.holds.filter((h) => !draft.holds.some((d) => d.seriesId === h.seriesId)),
        error: null,
    };
};

/**
 * Ends a 15-day hold and hands the slot back to auto-fill.
 *
 * `remove_ids` drops a series' override row for the rail: on a held series the
 * backend deletes the reorder row and the series falls back into auto-fill —
 * it is NOT excluded, because the rule only excludes ids that have no override
 * to begin with. The id must not appear in pins, series_ids or reorder in the
 * same request, so this body carries nothing else.
 *
 * @returns {{ body: object|null, error: string|null }}
 */
export const planReleaseHold = (state, seriesId) => {
    if (state?.configId == null) return { body: null, error: 'The rail has not loaded yet.' };
    const id = Number(seriesId);
    if (!state.holds.some((h) => h.seriesId === id)) return { body: null, error: 'That series is not on hold.' };
    return {
        body: { curation_type: CURATION_TYPE, curation_config_id: state.configId, remove_ids: [id] },
        error: null,
    };
};

/**
 * Checks a save did what it said: every pin in `body` came back at the position
 * it was sent for. A server that stores pins as a block from #1 lands them
 * elsewhere, and the admin has to hear that rather than read a success toast
 * naming a rank the rail doesn't hold.
 * @returns {string|null} what went wrong, or null when the save was honoured.
 */
export const misplacedPins = (body, data) => {
    if (!Array.isArray(body?.pins) || !body.pins.length || !data) return null;
    const saved = toPins(data);
    // A server that ignores `pins` wholesale tells us nothing about positions.
    if (!saved.length) return null;
    const wrong = body.pins.filter(({ series_id, position }) => {
        const at = saved.find((p) => p.seriesId === Number(series_id))?.position;
        return at !== undefined && at !== Number(position);
    });
    if (!wrong.length) return null;
    const where = wrong
        .map(({ series_id, position }) => `#${position} → #${saved.find((p) => p.seriesId === Number(series_id))?.position}`)
        .join(', ');
    return `The API did not keep the position that was sent (${where}). It stores pins from #1 down; exact pin positions need the backend's \`pins\` support.`;
};

/**
 * What the rail would look like with `draft` saved: pins and holds at their
 * slots, and the remaining preview rows (already in auto-fill order) filling
 * the free slots — the same merge the backend does.
 */
export const draftPreview = (draft, rows) => {
    const fixed = new Map([
        ...draft.pins.map((p) => [p.position, { seriesId: p.seriesId, isPinned: true, isHeld: false, expiresAt: null }]),
        ...draft.holds.map((h) => [h.position, { seriesId: h.seriesId, isPinned: false, isHeld: true, expiresAt: h.expiresAt }]),
    ]);
    const fixedIds = new Set([...fixed.values()].map((f) => f.seriesId));
    const byId = new Map(rows.map((r) => [r.seriesId, r]));
    const autos = rows.filter((r) => !fixedIds.has(r.seriesId));

    const result = [];
    let next = 0;
    POSITIONS.forEach((position) => {
        const slot = fixed.get(position);
        if (slot) {
            const row = byId.get(slot.seriesId) || { seriesId: slot.seriesId, title: '', thumbnail: '', publishAt: null };
            result.push({ ...row, ...slot, position });
        } else if (next < autos.length) {
            result.push({ ...autos[next++], position, isPinned: false, isHeld: false, expiresAt: null });
        }
    });
    return result;
};

const isResetBody = (body) =>
    Array.isArray(body.series_ids) && body.series_ids.length === 0
    && Array.isArray(body.excluded_ids) && body.excluded_ids.length === 0;

const validPosition = (position) => Number.isInteger(position) && position >= 1 && position <= RAIL_SIZE;

/**
 * @param state  output of `normaliseCuration`
 * @param intent one of
 *   { type: 'pin', seriesId, position }      permanent, at exactly `position`;
 *                                            moving an existing pin onto another swaps them
 *   { type: 'unpin', seriesId }
 *   { type: 'exclude', seriesId }            pins and holds included
 *   { type: 'restore', seriesId }            un-exclude
 *   { type: 'hold', seriesId, position }     15-day hold; re-sending restarts it
 *   { type: 'rename', name }
 *   { type: 'setActive', isActive }          rail on/off; overrides are kept
 *   { type: 'reset' }
 * @returns {{ body: object|null, evicted: object[], error: string|null }}
 *   `evicted` lists the active holds this change would end (the backend
 *   auto-evicts them), so the screen can warn before sending.
 */
export const planChange = (state, intent) => {
    const fail = (error) => ({ body: null, evicted: [], error });
    if (state?.configId == null) return fail('The rail has not loaded yet.');

    const { excludedIds, holds } = state;
    const base = { curation_type: CURATION_TYPE, curation_config_id: state.configId };
    const id = Number(intent?.seriesId);
    const position = intent?.position;
    const holdsAt = (pos) => holds.filter((h) => h.position === pos && h.seriesId !== id);

    let body;
    let evicted = [];

    switch (intent?.type) {
        case 'pin': {
            if (!validPosition(position)) return fail(`Position must be between 1 and ${RAIL_SIZE}.`);
            const current = pinPositionOf(state, id);
            const occupant = pinAt(state, position);
            const rest = pinsWithout(state, id);
            let next;
            if (occupant && occupant.seriesId !== id) {
                if (current === null) return fail(`#${position} is already pinned. Unpin it first, or pick another position.`);
                // Moving one pin onto another swaps the two.
                next = [
                    ...rest.filter((p) => p.seriesId !== occupant.seriesId),
                    { seriesId: occupant.seriesId, position: current },
                    { seriesId: id, position },
                ];
            } else {
                if (current === null && rest.length >= RAIL_SIZE) return fail(`All ${RAIL_SIZE} positions are pinned. Unpin one first.`);
                next = [...rest, { seriesId: id, position }];
            }
            body = { ...base, ...pinsBody(next) };
            if (excludedIds.includes(id)) body.excluded_ids = excludedIds.filter((x) => x !== id);
            evicted = holdsAt(position);
            break;
        }
        case 'unpin': {
            if (pinPositionOf(state, id) === null) return fail('That series is not pinned.');
            body = { ...base, ...pinsBody(pinsLeft(state, id)) };
            break;
        }
        case 'exclude': {
            if (excludedIds.includes(id)) return fail('That series is already excluded from the rail.');
            body = { ...base, excluded_ids: [...excludedIds, id] };
            if (pinPositionOf(state, id) !== null) Object.assign(body, pinsBody(pinsLeft(state, id)));
            break;
        }
        case 'restore': {
            if (!excludedIds.includes(id)) return fail('That series is not excluded from the rail.');
            body = { ...base, excluded_ids: excludedIds.filter((x) => x !== id) };
            break;
        }
        case 'hold': {
            if (!validPosition(position)) return fail(`Position must be between 1 and ${RAIL_SIZE}.`);
            const left = pinsLeft(state, id);
            if (left.some((p) => p.position === position)) {
                return fail(`#${position} is pinned. Unpin it first, or pick another position.`);
            }
            body = { ...base, reorder: [{ series_id: id, position }] };
            // A hold can't share the request with the same series in either list.
            if (pinPositionOf(state, id) !== null) Object.assign(body, pinsBody(left));
            if (excludedIds.includes(id)) body.excluded_ids = excludedIds.filter((x) => x !== id);
            evicted = holdsAt(position);
            break;
        }
        case 'setActive': {
            if (typeof intent.isActive !== 'boolean') return fail('Unknown change.');
            body = { ...base, is_active: intent.isActive };
            break;
        }
        case 'rename': {
            const error = validateRailName(intent.name);
            if (error) return fail(error);
            body = { ...base, name: String(intent.name).trim() };
            break;
        }
        case 'reset':
            return { body: { ...base, ...pinsBody([]), excluded_ids: [] }, evicted: holds, error: null };
        default:
            return fail('Unknown change.');
    }

    if (isResetBody(body)) return fail('This change would clear every override. Use Reset instead.');
    return { body, evicted, error: null };
};
